import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../App';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { User, Shield, BookOpen, Laptop, CheckCircle, XCircle, Clock, FileText, Eye, ChevronDown, ChevronUp } from 'lucide-react';
import Header from './Header';
import { formatDate, formatDateTime } from '../utils/dateFormatter';

// Using relative URLs

const EmployeePortal = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [portalData, setPortalData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [viewingPolicy, setViewingPolicy] = useState(null);
  const [showAccepted, setShowAccepted] = useState(false);

  const getPolicyName = (policy) => {
    // Fallback logic: try name first, then title, then 'Unknown'
    return policy.name || policy.title || 'Unknown';
  };

  useEffect(() => {
    fetchPortalData();
  }, []);

  const fetchPortalData = async () => {
    try {
      const [portalResponse, policiesResponse, trainingsResponse] = await Promise.all([
        axios.get(`/api/employee/portal`),
        axios.get(`/api/policies/my/pending`),
        axios.get(`/api/trainings/my/pending`)
      ]);
      
      const portal = portalResponse.data;
      const policiesData = policiesResponse.data;
      const trainingsData = trainingsResponse.data;
      
      setPortalData({
        ...portal,
        policies: {
          total: policiesData.pending.length + policiesData.accepted.length,
          accepted: policiesData.accepted.length,
          pending: policiesData.pending.length
        },
        trainings: {
          total: trainingsData.pending.length + trainingsData.completed.length,
          completed: trainingsData.completed.length,
          pending: trainingsData.pending.length
        },
        pending_policies: policiesData.pending || [],
        accepted_policies: policiesData.accepted || [],
        pending_trainings: trainingsData.pending || [],
        completed_trainings: trainingsData.completed || []
      });
    } catch (error) {
      toast.error('Failed to load portal data');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleAcceptPolicy = async (policyId) => {
    try {
      await axios.post(`/api/policies/${policyId}/accept`);
      toast.success('Policy accepted!');
      fetchPortalData();
    } catch (error) {
      toast.error('Failed to accept policy');
      console.error(error);
    }
  };

  const handleAcceptAllPolicies = async () => {
    try {
      const response = await axios.post(`/api/policies/accept-all`);
      toast.success(response.data.message || 'All policies accepted!');
      fetchPortalData();
    } catch (error) {
      toast.error('Failed to accept all policies');
      console.error(error);
    }
  };

  const handleViewPolicy = async (policyId) => {
    try {
      const response = await axios.get(`/api/policies/${policyId}`);
      setViewingPolicy(response.data);
    } catch (error) {
      toast.error('Failed to load policy');
      console.error(error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-appbg">
        <Header />
        <div className="flex items-center justify-center min-h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-navy-600"></div>
        </div>
      </div>
    );
  }

  const { employee, policies, trainings, device_compliance } = portalData || {};
  
  const getComplianceIcon = (value) => {
    return value ? (
      <CheckCircle className="w-5 h-5 text-navy-600" />
    ) : (
      <XCircle className="w-5 h-5 text-red-600" />
    );
  };

  const overallCompliant =
    device_compliance?.encryption_enabled &&
    device_compliance?.antivirus_installed &&
    device_compliance?.screen_lock_enabled;

  return (
    <div className="min-h-screen bg-appbg">
      <Header />
      
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-slate-900 mb-2">
            Welcome, {employee?.full_name || user.email}
          </h1>
          <p className="text-slate-600">Your personal compliance dashboard</p>
        </div>

        {/* Employee Info Card */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center">
              <User className="w-5 h-5 mr-2 text-navy-600" />
              Your Information
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <p className="text-sm text-slate-500">Email</p>
                <p className="font-medium">{employee?.email}</p>
              </div>
              <div>
                <p className="text-sm text-slate-500">Onboarding Date</p>
                <p className="font-medium">
                  {employee?.onboarding_date
                    ? formatDate(employee.onboarding_date)
                    : 'N/A'}
                </p>
              </div>
              <div>
                <p className="text-sm text-slate-500">Status</p>
                <Badge className="bg-emerald-100 text-emerald-800">
                  {employee?.status || 'Active'}
                </Badge>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          {/* Policies Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center text-lg">
                <Shield className="w-5 h-5 mr-2 text-blue-600" />
                Policies
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-slate-600">Total</span>
                  <span className="font-semibold">{policies?.total || 0}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-slate-600">Accepted</span>
                  <span className="font-semibold text-navy-600">{policies?.accepted || 0}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-slate-600">Pending</span>
                  <span className="font-semibold text-orange-600">{policies?.pending || 0}</span>
                </div>
              </div>
              {policies?.pending > 0 && (
                <div className="mt-4 p-3 bg-orange-50 rounded-lg border border-orange-200">
                  <p className="text-sm text-orange-800">
                    You have {policies.pending} pending {policies.pending === 1 ? 'policy' : 'policies'} to review
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Trainings Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center text-lg">
                <BookOpen className="w-5 h-5 mr-2 text-purple-600" />
                Trainings
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-slate-600">Total</span>
                  <span className="font-semibold">{trainings?.total || 0}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-slate-600">Completed</span>
                  <span className="font-semibold text-navy-600">{trainings?.completed || 0}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-slate-600">Pending</span>
                  <span className="font-semibold text-orange-600">{trainings?.pending || 0}</span>
                </div>
              </div>
              {trainings?.pending > 0 && (
                <div className="mt-4 p-3 bg-orange-50 rounded-lg border border-orange-200">
                  <p className="text-sm text-orange-800">
                    You have {trainings.pending} pending {trainings.pending === 1 ? 'training' : 'trainings'} to complete
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Device Compliance Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center text-lg">
                <Laptop className="w-5 h-5 mr-2 text-navy-600" />
                Device Compliance
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-slate-600">Encryption</span>
                  {getComplianceIcon(device_compliance?.encryption_enabled)}
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-slate-600">Antivirus</span>
                  {getComplianceIcon(device_compliance?.antivirus_installed)}
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-slate-600">Screen Lock</span>
                  {getComplianceIcon(device_compliance?.screen_lock_enabled)}
                </div>
              </div>
              <div className={`mt-4 p-3 rounded-lg border ${
                overallCompliant
                  ? 'bg-emerald-50 border-emerald-200'
                  : 'bg-red-50 border-red-200'
              }`}>
                <p className={`text-sm ${
                  overallCompliant ? 'text-emerald-800' : 'text-red-800'
                }`}>
                  {overallCompliant
                    ? '✓ Your device is compliant'
                    : '✗ Please ensure all security measures are enabled'}
                </p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Coming Soon Notice */}
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="pt-6">
            <div className="flex items-start">
              <Clock className="w-5 h-5 mr-3 text-blue-600 mt-1" />
              <div>
                <h3 className="font-semibold text-blue-900 mb-1">Device Compliance Coming Soon</h3>
                <p className="text-sm text-blue-800">
                  Automated device compliance tracking is being developed. 
                  Stay tuned for updates!
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Pending Trainings Section */}
        {portalData.pending_trainings && portalData.pending_trainings.length > 0 && (
          <Card className="mt-6">
            <CardHeader>
              <CardTitle className="flex items-center">
                <BookOpen className="w-5 h-5 mr-2 text-purple-600" />
                Pending Trainings
              </CardTitle>
              <CardDescription>
                Complete these trainings to maintain compliance
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {portalData.pending_trainings.map((training) => (
                <div key={training.id} className="p-4 border border-purple-200 bg-purple-50 rounded-lg">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h4 className="font-semibold text-slate-900 mb-1">{training.title}</h4>
                      {training.description && (
                        <p className="text-sm text-slate-600 mb-2">{training.description}</p>
                      )}
                      {training.frameworks && training.frameworks.length > 0 && (
                        <div className="flex flex-wrap gap-1 mb-3">
                          {training.frameworks.map((fw) => (
                            <Badge key={fw} variant="outline" className="text-xs">
                              {fw}
                            </Badge>
                          ))}
                        </div>
                      )}
                      <div className="flex items-center gap-3 text-xs text-slate-500">
                        <span className="flex items-center">
                          <Clock className="w-3 h-3 mr-1" />
                          {training.duration_minutes} minutes
                        </span>
                        <span>{training.questions?.length || 0} questions</span>
                      </div>
                    </div>
                    <Button
                      onClick={() => navigate(`/trainings/${training.id}/take`)}
                      className="ml-4 bg-navy-600 hover:bg-navy-700"
                      size="sm"
                    >
                      Start Training
                    </Button>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Completed Trainings Section */}
        {portalData.completed_trainings && portalData.completed_trainings.length > 0 && (
          <Card className="mt-6">
            <CardHeader>
              <div 
                className="flex items-center justify-between cursor-pointer"
                onClick={() => setShowAccepted(!showAccepted)}
              >
                <div>
                  <CardTitle className="flex items-center">
                    <CheckCircle className="w-5 h-5 mr-2 text-navy-600" />
                    Completed Trainings ({portalData.completed_trainings.length})
                  </CardTitle>
                  <CardDescription>
                    Trainings you have successfully completed
                  </CardDescription>
                </div>
                <Button variant="ghost" size="sm">
                  {showAccepted ? (
                    <ChevronUp className="w-5 h-5" />
                  ) : (
                    <ChevronDown className="w-5 h-5" />
                  )}
                </Button>
              </div>
            </CardHeader>
            {showAccepted && (
              <CardContent className="space-y-4">
                {portalData.completed_trainings.map((training) => (
                  <div 
                    key={training.id} 
                    className="p-4 border border-emerald-200 bg-emerald-50 rounded-lg hover:border-emerald-400 transition-all cursor-pointer"
                    onClick={() => navigate(`/trainings/${training.id}/result`)}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h4 className="font-semibold text-slate-900">{training.title}</h4>
                          <CheckCircle className="w-4 h-4 text-navy-600" />
                        </div>
                        {training.description && (
                          <p className="text-sm text-slate-600 mb-2">{training.description}</p>
                        )}
                        {training.frameworks && training.frameworks.length > 0 && (
                          <div className="flex flex-wrap gap-1 mb-3">
                            {training.frameworks.map((fw) => (
                              <Badge key={fw} variant="outline" className="text-xs">
                                {fw}
                              </Badge>
                            ))}
                          </div>
                        )}
                        <div className="space-y-1">
                          <p className="text-xs text-slate-500">
                            {training.duration_minutes} minutes • {training.questions?.length || 0} questions
                          </p>
                          {training.completed_at && (
                            <p className="text-xs text-navy-700 font-medium">
                              ✓ Completed on {formatDate(training.completed_at)} • Score: {training.score}% {training.passed && '(Passed)'}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                    <p className="text-xs text-slate-500 mt-2 italic">Click to view your answers and results</p>
                  </div>
                ))}
              </CardContent>
            )}
          </Card>
        )}

        {/* Pending Policies Section */}
        {portalData.pending_policies && portalData.pending_policies.length > 0 && (
          <Card className="mt-6">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center">
                    <FileText className="w-5 h-5 mr-2 text-orange-600" />
                    Pending Policy Acceptances
                  </CardTitle>
                  <CardDescription>
                    Please review and accept the following policies
                  </CardDescription>
                </div>
                <Button
                  onClick={handleAcceptAllPolicies}
                  className="bg-navy-600 hover:bg-navy-700"
                >
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Accept All Policies
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {portalData.pending_policies.map((policy) => (
                <div key={policy.id} className="p-4 border border-orange-200 bg-orange-50 rounded-lg">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h4 className="font-semibold text-slate-900 mb-1">{getPolicyName(policy)}</h4>
                      {policy.description && (
                        <p className="text-sm text-slate-600 mb-2">{policy.description}</p>
                      )}
                      {policy.frameworks && policy.frameworks.length > 0 && (
                        <div className="flex flex-wrap gap-1 mb-3">
                          {policy.frameworks.map((fw) => (
                            <Badge key={fw} variant="outline" className="text-xs">
                              {fw}
                            </Badge>
                          ))}
                        </div>
                      )}
                      <p className="text-xs text-slate-500">
                        Version {policy.version} • Published {formatDate(policy.published_at)}
                      </p>
                    </div>
                    <div className="ml-4 flex gap-2">
                      <Button
                        onClick={() => handleViewPolicy(policy.id)}
                        variant="outline"
                        size="sm"
                        className="border-blue-300 text-blue-700 hover:bg-blue-50"
                      >
                        <Eye className="w-4 h-4 mr-1" />
                        View
                      </Button>
                      <Button
                        onClick={() => handleAcceptPolicy(policy.id)}
                        className="bg-navy-600 hover:bg-navy-700"
                        size="sm"
                      >
                        Accept
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Accepted Policies Section */}
        {portalData.accepted_policies && portalData.accepted_policies.length > 0 && (
          <Card className="mt-6">
            <CardHeader>
              <div 
                className="flex items-center justify-between cursor-pointer"
                onClick={() => setShowAccepted(!showAccepted)}
              >
                <div>
                  <CardTitle className="flex items-center">
                    <CheckCircle className="w-5 h-5 mr-2 text-navy-600" />
                    Accepted Policies ({portalData.accepted_policies.length})
                  </CardTitle>
                  <CardDescription>
                    Policies you have already accepted
                  </CardDescription>
                </div>
                <Button variant="ghost" size="sm">
                  {showAccepted ? (
                    <ChevronUp className="w-5 h-5" />
                  ) : (
                    <ChevronDown className="w-5 h-5" />
                  )}
                </Button>
              </div>
            </CardHeader>
            {showAccepted && (
              <CardContent className="space-y-4">
                {portalData.accepted_policies.map((policy) => (
                  <div key={policy.id} className="p-4 border border-emerald-200 bg-emerald-50 rounded-lg">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h4 className="font-semibold text-slate-900">{getPolicyName(policy)}</h4>
                          <CheckCircle className="w-4 h-4 text-navy-600" />
                        </div>
                        {policy.description && (
                          <p className="text-sm text-slate-600 mb-2">{policy.description}</p>
                        )}
                        {policy.frameworks && policy.frameworks.length > 0 && (
                          <div className="flex flex-wrap gap-1 mb-3">
                            {policy.frameworks.map((fw) => (
                              <Badge key={fw} variant="outline" className="text-xs">
                                {fw}
                              </Badge>
                            ))}
                          </div>
                        )}
                        <div className="space-y-1">
                          <p className="text-xs text-slate-500">
                            Version {policy.version} • Published {formatDate(policy.published_at)}
                          </p>
                          {policy.accepted_at && (
                            <p className="text-xs text-navy-700 font-medium">
                              ✓ Accepted on {formatDateTime(policy.accepted_at)}
                            </p>
                          )}
                        </div>
                      </div>
                      <Button
                        onClick={() => handleViewPolicy(policy.id)}
                        variant="outline"
                        size="sm"
                        className="ml-4 border-blue-300 text-blue-700 hover:bg-blue-50"
                      >
                        <Eye className="w-4 h-4 mr-1" />
                        View
                      </Button>
                    </div>
                  </div>
                ))}
              </CardContent>
            )}
          </Card>
        )}

        {/* Policy Viewing Modal */}
        {viewingPolicy && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <Card className="max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
              <CardHeader className="border-b">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <CardTitle className="text-2xl mb-2">{viewingPolicy.name}</CardTitle>
                    {viewingPolicy.description && (
                      <CardDescription className="text-base">
                        {viewingPolicy.description}
                      </CardDescription>
                    )}
                    <div className="flex items-center gap-3 mt-3">
                      {viewingPolicy.frameworks && viewingPolicy.frameworks.map((fw) => (
                        <Badge key={fw} variant="outline">
                          {fw}
                        </Badge>
                      ))}
                      <span className="text-sm text-slate-500">Version {viewingPolicy.version}</span>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setViewingPolicy(null)}
                    className="text-slate-500 hover:text-slate-700"
                  >
                    ✕
                  </Button>
                </div>
              </CardHeader>
              
              <CardContent className="overflow-y-auto flex-1 py-6">
                {viewingPolicy.content ? (
                  <div className="prose max-w-none">
                    <div className="whitespace-pre-wrap text-slate-700">
                      {viewingPolicy.content}
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-12 text-slate-500">
                    <FileText className="w-16 h-16 mx-auto mb-4 opacity-30" />
                    <p>Policy content is not available.</p>
                  </div>
                )}
              </CardContent>

              <div className="border-t p-4 flex justify-end gap-3">
                <Button
                  variant="outline"
                  onClick={() => setViewingPolicy(null)}
                >
                  Close
                </Button>
                {portalData.pending_policies && 
                 portalData.pending_policies.some(p => p.id === viewingPolicy.id) && (
                  <Button
                    onClick={() => {
                      handleAcceptPolicy(viewingPolicy.id);
                      setViewingPolicy(null);
                    }}
                    className="bg-navy-600 hover:bg-navy-700"
                  >
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Accept Policy
                  </Button>
                )}
              </div>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};

export default EmployeePortal;
